import { Component } from '@theme/component';

/** Number of days before the popup is shown again after being dismissed. */
const REMEMBER_DAYS = 30;

/**
 * A custom element that shows a country selection popup on first visit.
 *
 * @typedef {object} Refs
 * @property {import('./dialog').DialogComponent} dialogComponent - The dialog component.
 * @property {HTMLSelectElement} select - The country select element.
 *
 * @extends {Component<Refs>}
 */
class CountryPopupComponent extends Component {
  requiredRefs = ['dialogComponent'];

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;
    if (this.#isRemembered()) return;

    setTimeout(() => this.refs.dialogComponent.showDialog(), 1000);
  }

  /**
   * Closes the popup without changing the country and remembers the dismissal.
   */
  dismiss = () => {
    this.remember();
    this.refs.dialogComponent.closeDialog();
  };

  /**
   * Sends the customer to the dedicated Brazil store instead of switching
   * the market on the current store.
   */
  goToBrazilStore = () => {
    this.remember();

    const url = this.dataset.brazilStoreUrl;
    if (url) window.location.href = url;
  };

  /**
   * Submits the form when a country is chosen from the dropdown, unless it's
   * Brazil, in which case the customer is redirected to the Brazil store.
   *
   * @param {Event} event - The change event.
   */
  selectFromDropdown = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.value) return;

    if (select.value === 'BR') {
      this.goToBrazilStore();
      return;
    }

    this.remember();
    select.form?.submit();
  };

  /**
   * Marks the popup as seen so it doesn't show again for a while.
   */
  remember = () => {
    try {
      localStorage.setItem(this.dataset.storageKey ?? '', String(Date.now()));
    } catch {
      // Ignore storage errors (e.g. private browsing).
    }
  };

  /**
   * Checks whether the popup was dismissed recently.
   *
   * @returns {boolean} True if the popup should stay hidden.
   */
  #isRemembered() {
    try {
      const seenAt = Number(localStorage.getItem(this.dataset.storageKey ?? ''));
      if (!seenAt) return false;

      const elapsedDays = (Date.now() - seenAt) / (1000 * 60 * 60 * 24);
      return elapsedDays < REMEMBER_DAYS;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('country-popup-component')) {
  customElements.define('country-popup-component', CountryPopupComponent);
}
